import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import type { Route } from 'nextjs-routes';
import { route } from 'nextjs-routes';

import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import Hint from 'ui/shared/Hint';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import TruncatedValue from 'ui/shared/TruncatedValue';

export type Props = {
  className?: string;
  label: string;
  value: string | React.ReactNode;
  valuePrefix?: string;
  valuePostfix?: string;
  hint?: string | React.ReactNode;
  isLoading?: boolean;
  diff?: string | number;
  diffFormatted?: string;
  diffPeriod?: '24h';
  period?: '1h' | '24h' | '30min';
  href?: Route;
  icon?: IconName;
};

const Container = ({ href, children }: { href?: Route; children: React.JSX.Element }) => {
  if (href) {
    return (
      <Link href={route(href)} variant="plain">
        {children}
      </Link>
    );
  }

  return children;
};

// 数字动画组件
const AnimatedNumber = ({ value, duration = 2500 }: { value: number; duration?: number }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValueRef = useRef(value);

  useEffect(() => {
    const prevValue = prevValueRef.current;
    const diff = value - prevValue;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // 使用 easeOutExpo 缓动函数使动画更平滑
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);
      const currentValue = prevValue + diff * easeOutExpo;

      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
    prevValueRef.current = value;
  }, [value, duration]);

  return <>{Math.round(displayValue).toLocaleString()}</>;
};

const StatsWidget = ({
  className,
  icon,
  label,
  value,
  valuePrefix,
  valuePostfix,
  isLoading,
  hint,
  diff,
  diffPeriod = '24h',
  diffFormatted,
  period,
  href,
}: Props) => {
  // 检查 value 是否为数字字符串
  const isNumericValue = typeof value === 'string' && !isNaN(Number(value.replace(/,/g, '')));
  const numericValue = isNumericValue ? Number(value.replace(/,/g, '')) : null;

  return (
    <Container href={!isLoading ? href : undefined}>
      <Flex
        className={className}
        alignItems="center"
        bgColor="transparent"
        p={3}
        borderRadius="base"
        justifyContent="space-between"
        columnGap={2}
        w="100%"
      >
        {icon && (
          <IconSvg
            name={icon}
            p={2}
            boxSize="40px"
            isLoading={isLoading}
            borderRadius="base"
            display={{ base: 'none', lg: 'block' }}
            flexShrink={0}
          />
        )}
        <Box w={{ base: '100%', lg: icon ? 'calc(100% - 48px)' : '100%' }}>
          <Skeleton
            loading={isLoading}
            color="text.secondary"
            textStyle="xs"
            w="fit-content"
          >
            <h2>{label}</h2>
          </Skeleton>
          <Skeleton
            loading={isLoading}
            display="flex"
            alignItems="baseline"
            fontWeight={500}
            textStyle="heading.md"
          >
            {valuePrefix && <chakra.span whiteSpace="pre">{valuePrefix}</chakra.span>}
            {isNumericValue ? (
              <AnimatedNumber value={numericValue!} />
            ) : typeof value === 'string' ? (
              <TruncatedValue isLoading={isLoading} value={value} />
            ) : (
              value
            )}
            {valuePostfix && <chakra.span whiteSpace="pre">{valuePostfix}</chakra.span>}
            {diff && Number(diff) > 0 && (
              <>
                <Text ml={2} mr={1} color="green.500">
                  +{diffFormatted || Number(diff).toLocaleString()}
                </Text>
                <Text color="text.secondary" textStyle="sm">({diffPeriod})</Text>
              </>
            )}
            {period && <Text color="text.secondary" textStyle="xs" fontWeight={400} ml={1}>({period})</Text>}
          </Skeleton>
        </Box>
        {typeof hint === 'string' ? (
          <Skeleton loading={isLoading} alignSelf="center" borderRadius="base">
            <Hint label={hint} boxSize={6} color={{ _light: 'gray.600', _dark: 'gray.400' }} />
          </Skeleton>
        ) : hint}
      </Flex>
    </Container>
  );
};

export default chakra(StatsWidget);
